import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, FileText, Trash, VideoCamera, PaperPlaneTilt, Play } from "@phosphor-icons/react";
import { toast } from "sonner";
import dayjs from "dayjs";
import { api, errMsg } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const inputCls = "w-full bg-[var(--bg)] border border-[var(--border-c)] rounded-[14px] px-4 py-2.5 text-sm outline-none focus:border-[var(--glow)]";

export default function CalendarScripts() {
  useSeo("Calendar & Active Scripts", "Plan filming and publish dates and manage your active video scripts.");
  const [events, setEvents] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", date: dayjs().format("YYYY-MM-DD"), type: "Film" });
  const [month, setMonth] = useState(dayjs().startOf("month"));

  const load = useCallback(async () => {
    const [c, s] = await Promise.all([api.get("/calendar"), api.get("/scripts")]);
    setEvents(c.data);
    setScripts(s.data);
  }, []);

  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, [load]);

  const addEvent = async (e) => {
    e.preventDefault();
    try {
      await api.post("/calendar", form);
      setOpen(false);
      setForm({ title: "", date: dayjs().format("YYYY-MM-DD"), type: "Film" });
      load();
      toast.success("Event scheduled");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const delEvent = async (id) => { await api.delete(`/calendar/${id}`); load(); };
  const delScript = async (id) => { await api.delete(`/scripts/${id}`); load(); toast.success("Script deleted"); };

  const daysInMonth = month.daysInMonth();
  const firstDow = month.day();
  const cells = [...Array(firstDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const eventsOn = (d) => events.filter((e) => dayjs(e.date).isSame(month.date(d), "day"));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-10 lg:py-14" data-testid="calendar-page">
      <div className="fade-up mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[var(--coral)] font-semibold mb-2">Plan & Write</p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight">Calendar & Scripts</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="add-event-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--glow)] text-white text-sm font-semibold hover:bg-[#6a4ce6] transition-colors">
              <Plus size={15} weight="bold" /> Schedule
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[var(--card)] border-[var(--border-c)]">
            <DialogHeader><DialogTitle>Schedule an event</DialogTitle></DialogHeader>
            <form onSubmit={addEvent} className="space-y-4">
              <input data-testid="event-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Event title" className={inputCls} />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="event-date-input" required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
                <select data-testid="event-type-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls}>
                  <option>Film</option>
                  <option>Publish</option>
                </select>
              </div>
              <button data-testid="event-save-btn" className="w-full py-2.5 rounded-[14px] bg-[var(--glow)] text-white font-semibold text-sm">Add to calendar</button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <section className="lg:col-span-3 surface rounded-[20px] p-6 fade-up-1">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold">{month.format("MMMM YYYY")}</h2>
            <div className="flex gap-2">
              <button data-testid="cal-prev-btn" onClick={() => setMonth(month.subtract(1, "month"))} className="w-8 h-8 rounded-full surface hover:border-[var(--glow)] text-sm">‹</button>
              <button data-testid="cal-next-btn" onClick={() => setMonth(month.add(1, "month"))} className="w-8 h-8 rounded-full surface hover:border-[var(--glow)] text-sm">›</button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-xs text-[var(--muted)] mb-2">
            {["sun", "mon", "tue", "wed", "thu", "fri", "sat"].map((d) => <div key={d}>{d[0].toUpperCase()}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((d, i) => {
              const evs = d ? eventsOn(d) : [];
              const isToday = d && month.date(d).isSame(dayjs(), "day");
              return (
                <div key={`${month.format("YYYY-MM")}-${i}`} className={`min-h-[52px] sm:min-h-[62px] rounded-[8px] sm:rounded-[10px] p-1 sm:p-1.5 text-[10px] sm:text-xs ${d ? "bg-[var(--bg)] border border-[var(--border-c)]" : ""} ${isToday ? "border-[var(--glow)]" : ""}`}>
                  {d && <p className={`font-semibold ${isToday ? "text-[var(--glow)]" : "text-[var(--muted)]"}`}>{d}</p>}
                  {evs.map((e) => (
                    <div key={e.id} className="mt-1 px-1 sm:px-1.5 py-0.5 rounded truncate text-[9px] sm:text-[10px] font-medium text-white group relative" style={{ background: e.type === "Film" ? "var(--coral)" : "var(--success)" }} title={e.title}>
                      {e.title}
                      <button data-testid={`event-delete-${e.id}`} onClick={() => delEvent(e.id)} className="hidden group-hover:block absolute -top-1 -right-1 bg-[var(--card)] rounded-full p-0.5"><Trash size={9} /></button>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
          <div className="flex gap-5 mt-4 text-xs text-[var(--muted)]">
            <span className="flex items-center gap-1.5"><VideoCamera size={13} color="var(--coral)" weight="fill" /> Film day</span>
            <span className="flex items-center gap-1.5"><PaperPlaneTilt size={13} color="var(--success)" weight="fill" /> Publish day</span>
          </div>
        </section>

        <section className="lg:col-span-2 surface rounded-[20px] p-6 fade-up-2" data-testid="active-scripts-section">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold flex items-center gap-2"><FileText size={20} color="var(--glow)" weight="duotone" /> Active Scripts</h2>
            <Link to="/scripts/new" data-testid="new-script-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--coral)] text-white text-xs font-semibold hover:opacity-90 transition-opacity">
              <Plus size={13} weight="bold" /> New script
            </Link>
          </div>
          {scripts.length === 0 && <p className="text-[var(--muted)] text-sm py-4" data-testid="scripts-empty">No scripts yet. Write your first hook.</p>}
          <div className="space-y-2.5">
            {scripts.map((s) => (
              <div key={s.id} className="bg-[var(--bg)] border border-[var(--border-c)] rounded-[14px] p-4 group lift" data-testid={`script-card-${s.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <Link to={`/scripts/${s.id}`} data-testid={`script-open-${s.id}`} className="min-w-0 flex-1">
                    <p className="font-semibold text-sm truncate">{s.title || "Untitled script"}</p>
                    <p className="text-xs text-[var(--muted)] mt-1 line-clamp-2">{s.hook || "No hook written yet"}</p>
                    <p className="text-[10px] text-[var(--muted)] mt-2">{s.mainPoints?.filter(Boolean).length || 0} points · updated {dayjs(s.updatedAt).format("MMM D")}</p>
                  </Link>
                  <div className="flex items-center gap-2 shrink-0">
                    <Link data-testid={`script-teleprompter-${s.id}`} to={`/teleprompter/${s.id}`} title="Open teleprompter" className="p-1 opacity-70 lg:opacity-0 lg:group-hover:opacity-100 text-[var(--muted)] hover:text-[var(--glow)] transition-opacity"><Play size={15} weight="fill" /></Link>
                    <button data-testid={`script-delete-${s.id}`} onClick={() => delScript(s.id)} className="p-1 opacity-70 lg:opacity-0 lg:group-hover:opacity-100 text-[var(--muted)] hover:text-[var(--coral)] transition-opacity"><Trash size={15} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
